import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { connectSocket, onSocketEvent } from '../services/socket';
import { leaveVideoSession } from './useVideoSession';
import { fetchCourseSlots } from '../redux/actions/zoom';
import { setSidebarTab, leaveMeeting } from '../redux/reducers/zoom';
import { showAppToast } from '../utils/appToast';

/** Capitaliza cada palabra de un nombre de persona (p. ej. «pato perez» → «Pato Perez»). */
const capitalizeName = (value) => String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toLocaleUpperCase('es-MX') + part.slice(1).toLocaleLowerCase('es-MX'))
    .join(' ');

const displayName = (user) => {
    const raw = user?.userName
        || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
        || 'Alguien';
    return capitalizeName(raw) || 'Alguien';
};

/**
 * Escucha en tiempo real:
 * - clase iniciada / terminada
 * - participantes que entran o salen de la videollamada
 */
export const useZoomSessionUpdates = (
    courseId,
    {
        inMeeting = false,
        currentLiveSessionId = null,
        currentUserId = null,
        activeSessionId = null,
        notifyLiveAvailable = true,
        onRemoteSessionEnded = null,
    } = {},
) => {
    const dispatch = useDispatch();
    const prevLiveSessionIdRef = useRef(currentLiveSessionId);
    const inMeetingRef = useRef(inMeeting);
    const activeSessionIdRef = useRef(activeSessionId);
    const currentUserIdRef = useRef(currentUserId);
    const notifyLiveAvailableRef = useRef(notifyLiveAvailable);
    const onRemoteEndedRef = useRef(onRemoteSessionEnded);

    inMeetingRef.current = inMeeting;
    activeSessionIdRef.current = activeSessionId;
    currentUserIdRef.current = currentUserId;
    notifyLiveAvailableRef.current = notifyLiveAvailable;
    onRemoteEndedRef.current = onRemoteSessionEnded;

    useEffect(() => {
        prevLiveSessionIdRef.current = currentLiveSessionId || null;
    }, [currentLiveSessionId]);

    useEffect(() => {
        if (!courseId) return undefined;

        let unsubs = [];
        let mounted = true;

        const forceLeaveCall = async ({ title, message, toastId }) => {
            await leaveVideoSession({ notify: false });
            dispatch(leaveMeeting());
            onRemoteEndedRef.current?.();
            if (title || message) {
                showAppToast({
                    tone: 'ended',
                    title,
                    message,
                    id: toastId || `zoom-ended-${courseId}`,
                    duration: 5500,
                });
            }
        };

        const setup = async () => {
            try {
                await connectSocket();
                if (!mounted) return;

                unsubs.push(onSocketEvent('zoom_session_update', (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;

                    const action = payload.action;

                    if (action === 'ended' || action === 'class_ended') {
                        const endedSessionId = payload.sessionId
                            ? String(payload.sessionId)
                            : null;
                        const wasInThisCall = inMeetingRef.current && (
                            !endedSessionId
                            || String(activeSessionIdRef.current) === endedSessionId
                        );
                        const classClosed = action === 'class_ended';
                        const endedByName = capitalizeName(payload.endedBy?.userName || '');
                        const toastId = `zoom-ended-${courseId}-${endedSessionId || 'all'}-${action}`;

                        dispatch(fetchCourseSlots(courseId)).then(() => {
                            if (!mounted) return;
                            if (classClosed || !wasInThisCall) {
                                prevLiveSessionIdRef.current = null;
                            }
                        });

                        if (wasInThisCall) {
                            void forceLeaveCall(
                                payload.isTest
                                    ? {
                                        title: 'Sesión terminada',
                                        message: endedByName
                                            ? `El anfitrión ${endedByName} cerró la sesión`
                                            : 'El anfitrión terminó la sesión',
                                        toastId,
                                    }
                                    : classClosed
                                        ? {
                                            title: 'Clase terminada',
                                            message: endedByName
                                                ? `${endedByName} cerró la clase`
                                                : 'El profesor terminó la clase',
                                            toastId,
                                        }
                                        : {
                                            title: 'Videollamada terminada',
                                            message: endedByName
                                                ? `${endedByName} terminó la videollamada`
                                                : 'El profesor terminó la videollamada',
                                            toastId,
                                        },
                            );
                        } else if (!inMeetingRef.current) {
                            showAppToast(
                                payload.isTest
                                    ? {
                                        tone: 'ended',
                                        title: 'Sesión terminada',
                                        message: 'La sesión ya no está disponible',
                                        id: toastId,
                                    }
                                    : classClosed
                                        ? {
                                            tone: 'ended',
                                            title: 'Clase terminada',
                                            message: 'La clase ya no está en curso',
                                            id: toastId,
                                        }
                                        : {
                                            tone: 'ended',
                                            title: 'Videollamada terminada',
                                            message: 'La videollamada ya no está disponible',
                                            id: toastId,
                                        },
                            );
                        }
                        return;
                    }

                    dispatch(fetchCourseSlots(courseId)).then((data) => {
                        if (!mounted || !data) return;

                        const liveId = data.currentLiveSession?._id
                            ? String(data.currentLiveSession._id)
                            : null;
                        const prevId = prevLiveSessionIdRef.current;
                        prevLiveSessionIdRef.current = liveId;

                        if (
                            liveId
                            && liveId !== prevId
                            && !inMeetingRef.current
                            && action === 'started'
                            && notifyLiveAvailableRef.current
                        ) {
                            dispatch(setSidebarTab('classes'));
                            const topic = data.currentLiveSession?.topic;
                            showAppToast({
                                tone: 'live',
                                title: data.currentLiveSession?.isTest
                                    ? 'Sesión disponible'
                                    : 'Clase en vivo',
                                message: topic
                                    ? `«${topic}» — únete ahora`
                                    : 'Únete ahora a la videollamada',
                                id: `zoom-live-${courseId}-${liveId}`,
                                duration: 6000,
                            });
                        }
                    });
                }));

                unsubs.push(onSocketEvent('zoom_participant_joined', (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;

                    const joinerId = payload?.user?._id
                        ? String(payload.user._id)
                        : null;
                    if (joinerId && joinerId === String(currentUserIdRef.current)) return;

                    // Solo avisar si hay llamada relevante (en vivo o misma sesión).
                    if (
                        inMeetingRef.current
                        || String(payload.sessionId) === String(prevLiveSessionIdRef.current)
                        || String(payload.sessionId) === String(activeSessionIdRef.current)
                    ) {
                        const name = displayName(payload.user);
                        showAppToast({
                            tone: 'joined',
                            title: name,
                            message: 'Se unió a la llamada',
                            id: `zoom-join-${courseId}-${joinerId || name}`,
                            duration: 3500,
                        });
                    }
                }));

                unsubs.push(onSocketEvent('zoom_participant_left', (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;

                    const leaverId = payload?.user?._id
                        ? String(payload.user._id)
                        : null;
                    if (leaverId && leaverId === String(currentUserIdRef.current)) return;

                    // Solo se avisa a quien sale por su cuenta: al terminar la clase o
                    // por desconexión saldrían todos y sería una ráfaga de avisos.
                    if (payload?.reason !== 'self') return;

                    if (inMeetingRef.current) {
                        const name = displayName(payload.user);
                        showAppToast({
                            tone: 'left',
                            title: name,
                            message: 'Salió de la llamada',
                            id: `zoom-leave-${courseId}-${leaverId || name}`,
                            duration: 3500,
                        });
                    }
                }));
            } catch (err) {
                console.warn('[Zoom] Socket no disponible para actualizaciones:', err.message);
            }
        };

        setup();

        return () => {
            mounted = false;
            unsubs.forEach((fn) => {
                try {
                    fn();
                } catch {
                    // el socket pudo haberse cerrado al cambiar de curso
                }
            });
            unsubs = [];
        };
    }, [courseId, dispatch]);
};
